import { type CSSProperties } from 'react';

import { type ShortUrlResponse } from '@air/api/types';

/** The wire union for `presentationSettings.pageWidth` — option values on authoring surfaces mirror it verbatim. */
export type ShareLinkPageWidth = NonNullable<NonNullable<ShortUrlResponse['presentationSettings']>['pageWidth']>;

/*
 * Product-confirmed pageWidth pixel values (COR-1948). Keyed by the full wire union so a new union
 * member is a type error here — the map and the renderer can't drift apart.
 */
const SHARE_LINK_PAGE_MAX_WIDTHS: Record<ShareLinkPageWidth, number> = {
  narrow: 960,
  standard: 1280,
  wide: 1600,
};

/*
 * The absent-value fallback for `presentationSettings.pageWidth` on collection surfaces. Collection
 * links saved before the field existed have no `pageWidth` and must keep rendering at the wide
 * width — `getShareLinkPageMaxWidth` below falls back through this same constant, so the public
 * renderer and the authoring surfaces (which default from it) can never disagree about what an
 * absent value renders as. (The public BOARD page has a different absent-value contract — unbounded,
 * see `boardPageColumn.ts` — so its style host never reaches this fallback.) Lives in the shared
 * share-link-design folder (not an authoring feature folder) so the public viewers don't import
 * from an authoring feature tree.
 */
export const PAGE_WIDTH_DEFAULT: ShareLinkPageWidth = 'wide';

// React's CSSProperties has no index signature for custom properties, so the var must be named
// explicitly for the style object to typecheck without an assertion.
type ShareLinkPageMaxWidthStyle = CSSProperties & {
  '--share-link-page-max-width': string;
};

const getShareLinkPageMaxWidth = (pageWidth: ShareLinkPageWidth | undefined): number => {
  // The wire value can outrun the compiled union (backend ships a new pageWidth option before this
  // frontend deploys), so an unmapped runtime value must resolve to a real width — never to
  // `undefined`, which would emit invalid CSS below and break the column. TS considers the trailing
  // `??` unreachable (the map is total over the union); it exists purely for that runtime skew.
  const width: number | undefined = SHARE_LINK_PAGE_MAX_WIDTHS[pageWidth ?? PAGE_WIDTH_DEFAULT];

  return width ?? SHARE_LINK_PAGE_MAX_WIDTHS[PAGE_WIDTH_DEFAULT];
};

// Set the returned style on the page's root div — the element wrapping the workspace header AND the
// content column — so the column classes built on the var (SHOWCASE_PAGE_COLUMN_CLASS_NAME and
// boardPageColumn's classes) can consume it from anywhere in the page without prop threading.
export const getShareLinkPageMaxWidthStyle = (
  pageWidth: ShareLinkPageWidth | undefined,
): ShareLinkPageMaxWidthStyle => ({
  '--share-link-page-max-width': `${getShareLinkPageMaxWidth(pageWidth)}px`,
});
